import {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export type UseWebmOpusRecorderPlaybackOptions = {
  blob: Blob | null;
  recordingDurationMs: number | null;
};

export type UseWebmOpusRecorderPlaybackResult = {
  audioRef: RefObject<HTMLAudioElement | null>;
  isPlaying: boolean;
  togglePlayback: () => void;
  currentTime: number;
  displayDuration: number;
  progressPercent: number;
  volume: number;
  isMuted: boolean;
  onSeekPercent: (value: number) => void;
  onVolumePercent: (value: number) => void;
  onToggleMute: () => void;
};

export function useWebmOpusRecorderPlayback({
  blob,
  recordingDurationMs,
}: UseWebmOpusRecorderPlaybackOptions): UseWebmOpusRecorderPlaybackResult {
  const [isPlaying, setIsPlaying] = useState(false);
  const [durationSec, setDurationSec] = useState(0);
  const [currentSec, setCurrentSec] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  const revokeObjectUrl = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!blob) {
      revokeObjectUrl();
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.removeAttribute('src');
        audioRef.current.load();
      }
      setIsPlaying(false);
      setDurationSec(0);
      setCurrentSec(0);
      setVolume(1);
      setIsMuted(false);
      return;
    }

    revokeObjectUrl();
    const url = URL.createObjectURL(blob);
    objectUrlRef.current = url;
    setDurationSec(0);
    setCurrentSec(0);
    const audio = audioRef.current;
    if (audio) {
      audio.src = url;
      audio.load();
    }
  }, [blob, revokeObjectUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !blob) {
      return;
    }
    audio.volume = volume;
    audio.muted = isMuted;
  }, [blob, volume, isMuted]);

  useEffect(() => {
    return () => {
      revokeObjectUrl();
    };
  }, [revokeObjectUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    const syncDuration = () => {
      const d = audio.duration;
      setDurationSec(Number.isFinite(d) && d > 0 ? d : 0);
    };

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onTimeUpdate = () => {
      setCurrentSec(audio.currentTime);
      syncDuration();
    };
    const onLoadedMeta = () => {
      syncDuration();
    };
    const onDurationChange = () => {
      syncDuration();
    };
    const onProgress = () => {
      syncDuration();
    };
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentSec(0);
    };
    const onVolumeChange = () => {
      setVolume(audio.volume);
      setIsMuted(audio.muted);
    };

    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMeta);
    audio.addEventListener('durationchange', onDurationChange);
    audio.addEventListener('progress', onProgress);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('volumechange', onVolumeChange);

    syncDuration();

    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMeta);
      audio.removeEventListener('durationchange', onDurationChange);
      audio.removeEventListener('progress', onProgress);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('volumechange', onVolumeChange);
    };
  }, [blob]);

  const togglePlayback = useCallback(() => {
    const audio = audioRef.current;
    if (!audio?.src) {
      return;
    }
    if (isPlaying) {
      audio.pause();
    } else {
      void audio.play().catch(() => {
        setIsPlaying(false);
      });
    }
  }, [isPlaying]);

  const displayDuration = useMemo(() => {
    if (Number.isFinite(durationSec) && durationSec > 0) {
      return durationSec;
    }
    if (
      recordingDurationMs != null &&
      Number.isFinite(recordingDurationMs) &&
      recordingDurationMs > 0
    ) {
      return recordingDurationMs / 1000;
    }
    return 0;
  }, [durationSec, recordingDurationMs]);

  const progressPercent = useMemo(() => {
    if (displayDuration <= 0) {
      return 0;
    }
    return (currentSec / displayDuration) * 100;
  }, [currentSec, displayDuration]);

  const onSeekPercent = useCallback(
    (value: number) => {
      const audio = audioRef.current;
      if (!audio) {
        return;
      }

      const rawDur = audio.duration;
      let dur = Number.isFinite(rawDur) && rawDur > 0 ? rawDur : 0;
      if (dur <= 0) {
        if (
          recordingDurationMs != null &&
          Number.isFinite(recordingDurationMs) &&
          recordingDurationMs > 0
        ) {
          dur = recordingDurationMs / 1000;
        }
      }
      if (dur <= 0) {
        return;
      }

      const newTime = (value / 100) * dur;
      audio.currentTime = newTime;
      setCurrentSec(newTime);
    },
    [recordingDurationMs]
  );

  const onVolumePercent = useCallback((value: number) => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    audio.volume = value / 100;
    setVolume(value / 100);
  }, []);

  const onToggleMute = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    audio.muted = !isMuted;
    setIsMuted(!isMuted);
  }, [isMuted]);

  return {
    audioRef,
    isPlaying,
    togglePlayback,
    currentTime: currentSec,
    displayDuration,
    progressPercent,
    volume,
    isMuted,
    onSeekPercent,
    onVolumePercent,
    onToggleMute,
  };
}
